import { useState, useEffect, useRef } from 'react'
import FireIcon from 'src/app/assets/icons/ico-fire.svg'
import type { StreakType } from '../StreakIndicator/StreakIndicator'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'

type Props = Readonly<{
  streakType?: StreakType | null
  streakBoostPercent?: number
}>

// Ring sizes matching the @repo/ui Rings component
const RING_SIZES = [78, 128, 168, 208]

export function MobileBoostIndicator({ streakType, streakBoostPercent }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const [showTooltip, setShowTooltip] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const hasStreak = streakType && streakBoostPercent && streakBoostPercent > 0

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowTooltip(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside as EventListener)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside as EventListener)
    }
  }, [showTooltip])

  // Don't render if no streak
  if (!hasStreak) return null

  const handleClick = () => {
    setShowTooltip(!showTooltip)
  }

  const isWinStreak = streakType === 'win'
  const streakLabel = isWinStreak ? t('Winning Streak') : t('Comeback Streak')
  const accentColorClass = isWinStreak ? 'streak-chip--win' : 'streak-chip--loss'

  return (
    <div className="streak-chip-wrapper" ref={wrapperRef}>
      <button type="button" className={`streak-chip ${accentColorClass}`} onClick={handleClick}>
        <div className="streak-chip__text">
          <span>+{streakBoostPercent}%</span>
        </div>
        <div className={`streak-chip__icon ${accentColorClass}`}>
          <FireIcon />
        </div>
        {/* Rings effect behind the chip */}
        <div className="streak-chip__rings">
          {RING_SIZES.map((size) => (
            <span
              key={size}
              className={`streak-chip__ring ${accentColorClass}`}
              style={{ width: size * 0.5, height: size * 0.5 }}
            />
          ))}
        </div>
      </button>
      {showTooltip && (
        <div className={`streak-chip-tooltip ${accentColorClass}`}>
          <span>
            {streakLabel}: +{streakBoostPercent}% {t('on next win')}
          </span>
        </div>
      )}
    </div>
  )
}
